import { createClient, type SupabaseClient } from "@supabase/supabase-js";

import type { LiveConfig } from "./live-config";

// Namespaced auth storage key so the console's session never collides with the pilot
// console (apps/web) or the mobile app on the same origin/device.
const AUTH_STORAGE_KEY = "secops.console.auth";

/**
 * Build the browser supabase-js client from an ALREADY-VALIDATED LiveConfig.
 *
 * The only sanctioned construction path: callers must pass a config that parseLiveConfig
 * accepted, so a secret-shaped key never reaches here. Auth is configured for the
 * "stay-logged-in" model (task 3): the session is persisted and silently refreshed.
 * `detectSessionInUrl` is on so passwordless sign-in and recovery redirects can complete.
 *
 * `storage` is injectable so tests run without a real browser localStorage and so the
 * stay-logged-in slice can supply a durable store explicitly. URL session detection is enabled
 * so Supabase email links and password-recovery links can hand sessions back to this console.
 */
export function createSupabaseClient(
  config: LiveConfig,
  options?: { storage?: SupabaseAuthStorage },
): SupabaseClient {
  return createClient(config.supabaseUrl, config.supabasePublishableKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: AUTH_STORAGE_KEY,
      ...(options?.storage ? { storage: options.storage } : {}),
    },
  });
}

/** Minimal storage shape supabase-js auth persistence requires (sync or async). */
export interface SupabaseAuthStorage {
  getItem: (key: string) => string | null | Promise<string | null>;
  setItem: (key: string, value: string) => void | Promise<void>;
  removeItem: (key: string) => void | Promise<void>;
}

export { AUTH_STORAGE_KEY };
